import { BAvatar, BDropdown } from "components/base";
import clsx from "clsx";
import { observer } from "mobx-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import EditProfileModal from "components/profile/EditProfileModal";
import { chatStore } from "store/chatStore";
import { userStore } from "store/userStore";
import { $urlImage } from "utils/url";
import SidebarAccountMenu from "./SidebarAccountMenu";
import "./SidebarFooter.scss";

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

type SidebarFooterProps = {
  collapsed?: boolean;
};

const SidebarFooter = observer(function SidebarFooter({
  collapsed = false,
}: SidebarFooterProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const displayName = userStore.info.name || userStore.info.username || "Người dùng";
  const initials = useMemo(() => getInitials(displayName), [displayName]);
  const avatarSrc = useMemo(
    () =>
      userStore.info.avatar ? $urlImage(userStore.info.avatar) : undefined,
    [userStore.info.avatar]
  );
  const emptyMenu = useMemo(() => ({ items: [] }), []);

  const handleMenuOpenChange = useCallback((nextOpen: boolean) => {
    setMenuOpen(nextOpen);
  }, []);

  const handleOpenProfile = useCallback(() => {
    setMenuOpen(false);
    setProfileOpen(true);
  }, []);

  const handleCloseProfile = useCallback(() => {
    setProfileOpen(false);
  }, []);

  const handleLogout = useCallback(() => {
    setMenuOpen(false);
    userStore.logout();
    chatStore.reset();
    navigate("/login", { replace: true });
  }, [navigate]);

  const renderAccountMenu = useCallback(
    () => (
      <SidebarAccountMenu
        onProfile={handleOpenProfile}
        onLogout={handleLogout}
      />
    ),
    [handleOpenProfile, handleLogout]
  );

  return (
    <>
      <BDropdown
        trigger={["click"]}
        placement="topLeft"
        open={menuOpen}
        onOpenChange={handleMenuOpenChange}
        popupRender={renderAccountMenu}
        menu={emptyMenu}
        overlayClassName="sidebar-footer__dropdown"
      >
        <button
          type="button"
          className={clsx(
            "sidebar-footer",
            collapsed && "sidebar-footer--collapsed"
          )}
        >
          <BAvatar
            className="sidebar-footer__avatar"
            size="small"
            src={avatarSrc}
          >
            {initials}
          </BAvatar>
          {!collapsed ? (
            <div className="sidebar-footer__info">
              <span className="sidebar-footer__name">{displayName}</span>
            </div>
          ) : null}
        </button>
      </BDropdown>
      <EditProfileModal open={profileOpen} onClose={handleCloseProfile} />
    </>
  );
});

export default SidebarFooter;
